import type { ContainerType } from '../../types';

export const SLOT_W = 40;
export const SLOT_D = 88;

const normalizeSlotCoord = (value: number) => Math.round(value * 100) / 100;

export function getSlotKey(col: number, row: number): string {
  return `${normalizeSlotCoord(col)}-${normalizeSlotCoord(row)}`;
}

export function getTopTier(containers: Container[], slotKey: SlotKey): number {
  return containers
    .filter((c) => c.slotKey === slotKey)
    .reduce((prev, curr) => {
      if (curr.tier > prev) return Number(curr.tier);

      return prev;
    }, 1);
}

export type SlotKey = string;

export type Container = {
  id: string;
  slotKey: SlotKey;
  slotKeys?: SlotKey[];
  number: string;
  tier: number;
  length?: number;
  type?: ContainerType;
  client?: {
    code: string;
  };
};

export type BlockMap = {
  id: string;
  startCol: number;
  startRow: number;
  colsCount: number;
  rowsCount: number;
  rotation: number;
  roads?: ('top' | 'right' | 'bottom' | 'left')[];
};

export type SlotsState = {
  containers: Container[];
  slotNames?: Record<SlotKey, string>;
  blockMaps?: BlockMap[];
  /** Аналог старого VISIBLE_SLOTS: какие имена слотов считаются доступными */
  visibleSlotNames?: string[];
};

export function createInitialSlotsState(): SlotsState {
  return {
    containers: [],
    slotNames: undefined,
    visibleSlotNames: undefined,
  };
}

export function parseSlotKey(key: SlotKey): { col: number; row: number } {
  const match = /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/.exec(key);
  if (!match) {
    return { col: 0, row: 0 };
  }
  return {
    col: Number(match[1]),
    row: Number(match[2]),
  };
}

export function parseSlotLabel(label: string): { col: number; row: number } {
  const match = /.*\((-?\d+),(-?\d+)\)\s*$/.exec(label);
  if (!match) {
    return { col: 0, row: 0 };
  }
  return {
    col: Number(match[1]),
    row: Number(match[2]),
  };
}

function formatSlotLabelFromKey(key: SlotKey): string {
  const { col, row } = parseSlotKey(key);
  return `(${col},${row})`;
}

export function getAllSlotCoords(state: SlotsState): { col: number; row: number }[] {
  const seen = new Set<SlotKey>();
  const result: { col: number; row: number }[] = [];

  for (const container of state.containers) {
    if (seen.has(container.slotKey)) continue;
    seen.add(container.slotKey);
    result.push(parseSlotKey(container.slotKey));
  }

  return result;
}

export function getContainersInSlot(state: SlotsState, col: number, row: number): Container[] {
  const key = getSlotKey(col, row);
  return state.containers
    .filter((c) => c.slotKeys?.includes(key) ?? c.slotKey === key)
    .slice()
    .sort((a, b) => (a.tier ?? 0) - (b.tier ?? 0));
}

export function getStackCount(state: SlotsState, key: SlotKey): number {
  return state.containers.filter((c) => c.slotKeys?.includes(key) ?? c.slotKey === key).length;
}

export function isUsed(state: SlotsState, key: SlotKey): boolean {
  return getStackCount(state, key) > 0;
}

/** Отображаемое имя слота (в Edit при swap меняется вместе с содержимым) */
export function getSlotDisplayName(state: SlotsState, key: SlotKey): string {
  return state.slotNames?.[key] ?? formatSlotLabelFromKey(key);
}

export function getTopContainerInSlot(
  state: SlotsState,
  col: number,
  row: number,
): Container | undefined {
  const list = getContainersInSlot(state, col, row);
  if (!list.length) return undefined;
  return list[list.length - 1];
}

export function moveSlot(
  state: SlotsState,
  fromCol: number,
  fromRow: number,
  toCol: number,
  toRow: number,
): SlotsState {
  const fromKey = getSlotKey(fromCol, fromRow);
  const toKey = getSlotKey(toCol, toRow);
  if (fromKey === toKey) return state;

  const containers = state.containers.map((c) =>
    c.slotKey === fromKey ? { ...c, slotKey: toKey } : c,
  );

  const slotNames: Record<SlotKey, string> = { ...(state.slotNames || {}) };
  if (fromKey in slotNames) {
    slotNames[toKey] = slotNames[fromKey];
    delete slotNames[fromKey];
  }

  return {
    containers,
    slotNames: Object.keys(slotNames).length ? slotNames : undefined,
    visibleSlotNames: state.visibleSlotNames,
  };
}

export function moveContainer(
  state: SlotsState,
  containerId: string,
  toCol: number,
  toRow: number,
): SlotsState {
  const toKey = getSlotKey(toCol, toRow);

  const topTier = getTopTier(state.containers, toKey);

  const containers = state.containers.map((c) =>
    c.id === containerId ? { ...c, slotKey: toKey, tier: topTier } : c,
  );

  return {
    containers,
    slotNames: state.slotNames,
    visibleSlotNames: state.visibleSlotNames,
  };
}

export function isHidden(state: SlotsState, key: string): boolean {
  const name = getSlotDisplayName(state, key);
  if (!state.visibleSlotNames) return false;
  return !state.visibleSlotNames.includes(name);
}

export function setSlotName(state: SlotsState, col: number, row: number, name: string): SlotsState {
  const key = getSlotKey(col, row);
  const slotNames: Record<SlotKey, string> = {
    ...(state.slotNames || {}),
    [key]: name,
  };

  const visibleSlotNamesSet = new Set(state.visibleSlotNames ?? []);
  visibleSlotNamesSet.add(name);

  return {
    ...state,
    slotNames,
    visibleSlotNames: Array.from(visibleSlotNamesSet),
  };
}
